/**
 * Note helpers — thin shims over `@tonado/core`'s canonical implementation.
 *
 * The dApp previously maintained its own (incompatible) note format,
 * missing the `0x` prefix and bypassing core's field-range rejection
 * sampling. This shim re-exports core's API and adds a sync metadata-only
 * extractor for UI display (`parseNoteMetadata`) — the full async
 * `parseNote` is used at withdraw time when we need the commitment.
 */
import {
  parseNote as coreParseNote,
  buildNote as coreBuildNote,
  generateNote as coreGenerateNote,
  isValidNote as coreIsValidNote,
  noteRegex,
  type ParsedNote,
} from "@tonado/core";

export type Note = {
  raw: string;
  asset: string;
  amount: string;
  network: "mainnet" | "testnet";
  hex: string; // pre-image hex, 124 chars (31-byte nullifier ‖ 31-byte secret)
};

/**
 * Sync metadata-only parser for UI rendering. Doesn't compute Poseidon
 * commitments. Returns null on malformed input.
 *
 * For actual deposit/withdraw flows, use the async `parseNote` (which is
 * `@tonado/core`'s real implementation).
 */
export function parseNoteMetadata(raw: string): Note | null {
  const trimmed = raw.trim();
  const m = noteRegex().exec(trimmed);
  if (!m) return null;
  const [, assetId, amount, network, hexWith0x] = m;
  return {
    raw: trimmed,
    asset: assetId,
    amount,
    network: network as "mainnet" | "testnet",
    hex: hexWith0x.startsWith("0x") ? hexWith0x.slice(2) : hexWith0x,
  };
}

export function isValidNote(raw: string): boolean {
  return coreIsValidNote(raw.trim());
}

/** Re-export the async, Poseidon-aware parser for deposit/withdraw flows. */
export const parseNote: (raw: string) => Promise<ParsedNote> = coreParseNote;
export const buildNote = coreBuildNote;
export const generateNote = coreGenerateNote;
export type { ParsedNote };
